import * as tslib_1 from "tslib";
import { NgZone, } from '@angular/core';
import { AppStateFacade, Setting, } from '@app-state';
import { FeatureFlag, } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { WidgetResource } from '@pages/monitoring/types';
import { ServerConfiguration } from '@services/configuration/server.configuration';
import { FOLDER_HEADER_NAME, FOLDER_PARAM_NAME, LOCALIZATION_HEADER_NAME, LOCALIZATION_PARAM_NAME, } from '@services/generics/constants';
import { Mapper } from '@services/generics/data.mapper';
import { Logger } from '@services/logger/abstract.logger';
import { toDate } from '@utils/object.utils';
import { fibonacci, } from 'backoff';
import cloneDeep from 'lodash-es/cloneDeep';
import difference from 'lodash-es/difference';
import intersection from 'lodash-es/intersection';
import { BehaviorSubject, forkJoin, fromEvent, merge, NEVER, Subject, } from 'rxjs';
import { filter, map, pairwise, switchMap, takeUntil, tap, } from 'rxjs/operators';
import { MapperService } from './mapper.service';
import * as i0 from "@angular/core";
import * as i1 from "./configuration/server.configuration";
import * as i2 from "../app-state/app-state.facade";
import * as i3 from "./logger/abstract.logger";
import * as i4 from "@ngx-translate/core";
var RETRY_AFTER = 300;
var RETRY_AFTER_MAX = 60 * 1000;
var RETRY_BACKOFF_CONFIG = {
    randomisationFactor: .1,
    initialDelay: RETRY_AFTER,
    maxDelay: RETRY_AFTER_MAX,
};
// protect from Azure SignalR poisioned messages
jQuery.signalR.prototype.received = function (callback) {
    var connection = this;
    jQuery(connection).bind(jQuery.signalR.events.onReceived, function (_, data) {
        try {
            callback.call(connection, data);
        }
        catch (e) {
            console.error(e);
        }
    });
    return connection;
};
var SignalRService = /** @class */ (function (_super) {
    tslib_1.__extends(SignalRService, _super);
    function SignalRService(_serverConfig, _appState, _logger, _ngZone, _translateService) {
        var _this = _super.call(this) || this;
        _this._appState = _appState;
        _this._logger = _logger;
        _this._ngZone = _ngZone;
        _this._translateService = _translateService;
        _this.jobs$ = new Subject();
        _this.sessions$ = new Subject();
        _this.robotLicenses$ = new Subject();
        _this.license$ = new Subject();
        _this.licenseUsage$ = new Subject();
        _this.notification$ = new Subject();
        _this.livefeedMessages$ = new Subject();
        _this.healthState$ = new Subject();
        _this._robots = [];
        _this._jobs = [];
        _this._watched = new Set();
        _this._disconnected$ = new Subject();
        _this._monitoringFilter = null;
        // connection definitions
        _this._connections = {
            browser: {
                hub: 'browserSignalrEventHub',
                socket: null,
                proxy: null,
                connected$: new Subject(),
                isDisconnected: true,
                backoff: fibonacci(RETRY_BACKOFF_CONFIG),
                shouldReWatch: true,
            },
            monitoring: {
                hub: 'monitoringHub',
                socket: null,
                proxy: null,
                connected$: new BehaviorSubject(false),
                isDisconnected: true,
                backoff: fibonacci(RETRY_BACKOFF_CONFIG),
                shouldReWatch: true,
            },
            callbacks: {
                onDisconnect: function (connection) {
                    // don't reconnect on force close
                    if (connection.isDisconnected) {
                        _this._logger.log('SignalR flagged to disconnect. Skipping retry...');
                        connection.backoff.reset();
                    }
                    else {
                        try {
                            connection.backoff.backoff();
                        }
                        catch (e) {
                            // the backoff() method will only throw if there's already a backoff
                            // operation in progress, case in which we don't need to do anything
                            _this._logger.error('SignalR already has a backoff in progress, ignoring...', e);
                        }
                    }
                },
                onStart: function (connection) {
                    // the disconnectTimeout is set to 30s by the server,
                    // therefore we override it after the connection starts.
                    // by setting it to 0 we override SignalR's default reconnect spamming
                    connection.socket.disconnectTimeout = 0;
                    if (!connection.isDisconnected) {
                        connection.backoff.reset();
                        if (connection.shouldReWatch) {
                            _this._watch(Array.from(_this._watched));
                        }
                    }
                    connection.connected$.next(true);
                    connection.isDisconnected = false;
                    if (connection.hub === 'monitoringHub' && _this._monitoringFilter) {
                        _this.monitoringSubscribe(_this._monitoringFilter);
                    }
                },
            },
        };
        _this.monitoringConnect = function (locale, reconnect) {
            var _a;
            if (reconnect === void 0) { reconnect = false; }
            if (_this._appState.user.loggedInAsHost ||
                !_this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring) ||
                !_this._appState.user.folder.hasAny) {
                return;
            }
            if (reconnect) {
                // synchronously stop the current SignalR socket
                _this.monitoringDisconnect(false);
            }
            // setup the necessary headers for connect
            var headers = (_a = {},
                _a[LOCALIZATION_HEADER_NAME] = locale,
                _a);
            if (_this._appState.user.folder.hasAny) {
                headers[FOLDER_HEADER_NAME] = "" + _this._folderId;
            }
            jQuery.ajaxSetup({ headers: headers });
            _this._connections.monitoring.socket.start({ transport: 'webSockets' }, function () { return _this._ngZone.run(function () {
                _this._connections.callbacks.onStart(_this._connections.monitoring);
            }); });
        };
        _this.monitoringDisconnect = function (async) {
            if (async === void 0) { async = true; }
            if (_this._connections.monitoring.socket &&
                _this._connections.monitoring.socket.state === 1 /* Connected */) {
                _this._connections.monitoring.isDisconnected = true;
                _this._connections.monitoring.socket.stop(async);
            }
        };
        _this.monitoringSubscribe = function (filters) {
            var _a;
            if (_this._appState.user.loggedInAsHost ||
                !_this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring)) {
                return;
            }
            if (!!filters) {
                _this._monitoringFilter = filters;
            }
            if (!_this._connections.monitoring.isDisconnected &&
                _this._connections.monitoring.socket &&
                _this._connections.monitoring.socket.state === 1 /* Connected */) {
                var options = (_a = {},
                    _a[FOLDER_PARAM_NAME] = _this._folderId,
                    _a[LOCALIZATION_PARAM_NAME] = _this._translateService.currentLang,
                    _a);
                _this._connections.monitoring.proxy.invoke('subscribeTo', options, _this._monitoringFilter);
            }
        };
        _this._onBackOffReady = function (connection, delay) {
            if (!connection.isDisconnected) {
                _this._logger.error("SignalR disconnected! Trying to reconnect in " + delay + "ms!");
                var attemptReconnect = connection.socket.start();
                attemptReconnect.done(function () { return _this._ngZone.run(function () { return _this._connections.callbacks.onStart(connection); }); });
            }
        };
        _this._nextInZone = function (subject) { return function (message) { return _this._ngZone.run(function () { return subject.next(message); }); }; };
        var normalizedBaseUrl = _serverConfig.baseHref.endsWith('/') ?
            _serverConfig.baseHref.substring(0, _serverConfig.baseHref.length - 1) :
            _serverConfig.baseHref;
        var monitoringUrl = normalizedBaseUrl + _this._appState.settings.get(Setting.monitoringSignalrUrl);
        _this._connections.browser.socket = jQuery.hubConnection(normalizedBaseUrl);
        _this._connections.monitoring.socket = jQuery.hubConnection(monitoringUrl, { useDefaultPath: false });
        _this._connections.browser.socket.disconnected(function () { return _ngZone.run(function () { return _this._connections.callbacks.onDisconnect(_this._connections.browser); }); });
        _this._connections.monitoring.socket.disconnected(function () { return _ngZone.run(function () { return _this._connections.callbacks.onDisconnect(_this._connections.monitoring); }); });
        _this._connections.browser.proxy = _this._connections.browser.socket.createHubProxy('browserSignalrEventHub');
        _this._connections.monitoring.proxy = _this._connections.monitoring.socket.createHubProxy('monitoringHub');
        _this._connections.browser.backoff.on('ready', function (_number, delay) { return _ngZone.run(function () { return _this._onBackOffReady(_this._connections.browser, delay); }); });
        _this._connections.monitoring.backoff.on('ready', function (_number, delay) { return _ngZone.run(function () { return _this._onBackOffReady(_this._connections.monitoring, delay); }); });
        return _this;
    }
    Object.defineProperty(SignalRService.prototype, "_tenantId", {
        get: function () {
            return this._appState.user.value.tenancyId;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SignalRService.prototype, "_folderId", {
        get: function () {
            return this._appState.user.folder.value.id;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SignalRService.prototype, "watched", {
        get: function () {
            return cloneDeep(this._watched);
        },
        set: function (watched) {
            this._watched = watched;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Initiate the signalR connections
     * and hook the appropiate event handlers
     * @memberof SignalRService
     */
    SignalRService.prototype.connect = function () {
        var _this = this;
        if (this._appState.user.loggedInAsHost) {
            return;
        }
        if (!this._connections.browser.proxy ||
            !this._connections.monitoring.proxy ||
            !this._connections.browser.socket) {
            throw new Error('Unable to connect this._connections');
        }
        // browser hub events
        var browserProxy = this._connections.browser.proxy;
        var updateSessions$ = fromEvent(browserProxy, 'updateSessions').pipe(map(function (message) { return toDate(_this._toDto(message, MapperService.sessionUpdatedMap)); }), tap(this._nextInZone(this.sessions$)));
        var updateRobotLicenses$ = fromEvent(browserProxy, 'updateRobotLicenses').pipe(map(function (message) { return toDate(message); }), tap(this._nextInZone(this.robotLicenses$)));
        var updateLicense$ = fromEvent(browserProxy, 'updateLicense').pipe(tap(function () { return _this._ngZone.run(function () { return _this.license$.next(); }); }));
        var updateLicenses$ = fromEvent(browserProxy, 'updateLicenses').pipe(map(function (message) { return toDate(message); }), tap(this._nextInZone(this.licenseUsage$)));
        var updateJobs$ = fromEvent(browserProxy, 'updateJobs').pipe(map(function (message) { return toDate(message); }), tap(this._nextInZone(this.jobs$)));
        var updatePermissions$ = fromEvent(browserProxy, 'updatePermissions').pipe(filter(function () { return !!_this._appState.user.value; }), filter(function (message) { return !message.userIds || message.userIds.indexOf(_this._appState.user.value.id) !== -1; }), switchMap(function () { return forkJoin([
            _this._appState.user.permissions.fetch(),
            _this._appState.user.folder.hasAny ?
                NEVER :
                // the list of folders might have changed
                _this._appState.user.folder.fetch(),
        ]); }));
        // abp common events
        var getNotification$ = fromEvent(browserProxy, 'getNotification').pipe(map(function (message) {
            var notification = message.notification;
            notification.message = notification.data.message;
            notification.name = notification.notificationName;
            notification.timestamp = notification.creationTime;
            return notification;
        }), tap(this._nextInZone(this.notification$)));
        // monitoring events
        var monitoringProxy = this._connections.monitoring.proxy;
        var onLiveFeedMessage$ = fromEvent(monitoringProxy, 'onLiveFeedMessage').pipe(tap(this._nextInZone(this.livefeedMessages$)));
        var healthStateUpdates$ = [
            { event: 'onRobotsHealthStateChanged', target: WidgetResource.Robot },
            { event: 'onJobsHealthStateChanged', target: WidgetResource.Job },
            { event: 'onQueuesHealthStateChanged', target: WidgetResource.Queue },
        ].map(function (_a) {
            var target = _a.target, event = _a.event;
            return fromEvent(monitoringProxy, event).pipe(map(function (message) { return toDate(message); }), tap(function (update) { return _this._ngZone.run(function () { return _this.healthState$.next({
                target: target,
                update: update,
            }); }); }));
        });
        merge.apply(void 0, [updateSessions$,
            updateRobotLicenses$,
            updateLicense$,
            updateLicenses$,
            updateJobs$,
            updatePermissions$,
            getNotification$,
            onLiveFeedMessage$].concat(healthStateUpdates$)).pipe(takeUntil(this._disconnected$)).subscribe();
        this._connections.browser.socket.start().done(function () { return _this._ngZone.run(function () {
            return _this._connections.callbacks.onStart(_this._connections.browser);
        }); });
        this.monitoringConnect(this._translateService.currentLang);
        this._appState.user.folder.value$
            .pipe(pairwise(), filter(function (_a) {
            var oldOu = _a[0], newOu = _a[1];
            return !!oldOu && !!newOu;
        }), takeUntil(this._disconnected$))
            .subscribe(function (_a) {
            var oldOu = _a[0], newOu = _a[1];
            var watched = Array.from(_this._watched);
            var toUnsubscribe = watched.filter(function (topic) { return topic.startsWith(_this._tenantId + "_" + oldOu.id + "_"); });
            var toSubscribe = toUnsubscribe.map(function (topic) { return topic.replace(_this._tenantId + "_" + oldOu.id + "_", _this._tenantId + "_" + newOu.id + "_"); });
            _this._watch(toUnsubscribe, false);
            _this._watch(toSubscribe, true);
        });
    };
    /**
     * Stop all active signalR connections
     * and complete all streams
     * @memberof SignalRService
     */
    SignalRService.prototype.disconnect = function () {
        this._robots = [];
        this._jobs = [];
        this._watched.clear();
        this._disconnected$.next();
        if (this._connections.browser.socket &&
            this._connections.browser.socket.state === 1 /* Connected */) {
            this._connections.browser.isDisconnected = true;
            this._connections.browser.socket.stop();
        }
        if (this._connections.monitoring.socket &&
            this._connections.monitoring.socket.state === 1 /* Connected */) {
            this._connections.monitoring.isDisconnected = true;
            this._connections.monitoring.socket.stop();
        }
    };
    /**
     * Start/stop watching robot groups
     * Removed/added groups are automatically determined
     * @param {(number[] | false)} ids
     * The robot identifiers that need to be watched.
     * Passing a falsy value will terminate all active robot subscriptions.
     * @memberof SignalRService
     */
    SignalRService.prototype.watchRobots = function (ids) {
        var _this = this;
        ids = ids || [];
        var robotKeygen = function (id) { return _this._tenantId + "_Robot_" + id; };
        var robots = this._watchMap(this._robots, ids, robotKeygen);
        this._watch(robots.remove, false);
        this._watch(robots.add, true);
        this._robots = robots.watched;
    };
    /**
     * Start/stop watching license groups
     * Removed/added groups are automatically determined
     * @param {(number[] | false)} ids
     * The license identifiers that need to be watched.
     * Passing a falsy value will terminate all active license subscriptions.
     * @memberof SignalRService
     */
    SignalRService.prototype.watchLicenses = function (ids) {
        var _this = this;
        ids = ids || [];
        var licenseKeygen = function (id) { return _this._tenantId + "_License_" + id; };
        var licenses = this._watchMap(this._robots, ids, licenseKeygen);
        this._watch(licenses.remove, false);
        this._watch(licenses.add, true);
    };
    /**
     * Start/stop watching for newly added robots.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    SignalRService.prototype.watchNewRobots = function (watch) {
        if (watch === void 0) { watch = true; }
        this._watch([this._tenantId + "_" + this._folderId + "_RobotsAdded"], watch);
    };
    /**
     * Listen for events from all robots.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    SignalRService.prototype.watchAllRobots = function (watch) {
        if (watch === void 0) { watch = true; }
        this._watch([this._tenantId + "_" + this._folderId + "_RobotsAll"], watch);
    };
    /**
     * Start/stop watching job groups
     * Removed/added groups are automatically determined
     * @param {(number[] | false)} ids
     * The job identifiers that need to be watched.
     * Passing a falsy value will terminate all active job subscriptions.
     * @memberof SignalRService
     */
    SignalRService.prototype.watchJobs = function (ids) {
        var _this = this;
        ids = ids || [];
        var jobKeygen = function (id) { return _this._tenantId + "_Job_" + id; };
        var jobs = this._watchMap(this._jobs, ids, jobKeygen);
        this._watch(jobs.remove, false);
        this._watch(jobs.add, true);
        this._jobs = jobs.watched;
    };
    /**
     * Start/stop watching for newly added jobs.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    SignalRService.prototype.watchNewJobs = function (watch) {
        if (watch === void 0) { watch = true; }
        this._watch([this._tenantId + "_" + this._folderId + "_JobsAdded"], watch);
    };
    /**
     * Listen for events from all jobs.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    SignalRService.prototype.watchAllJobs = function (watch) {
        if (watch === void 0) { watch = true; }
        this._watch([this._tenantId + "_" + this._folderId + "_JobsAll"], watch);
    };
    /**
     * Start/stop watching for permission changes.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    SignalRService.prototype.watchPermissions = function (watch) {
        if (watch === void 0) { watch = true; }
        this._watch([this._tenantId + "_PermissionsAll"], watch);
    };
    /**
     * Listen for events from all licenses.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    SignalRService.prototype.watchAllLicenses = function (watch) {
        if (watch === void 0) { watch = true; }
        this._watch([this._tenantId + "_LicensesAll"], watch);
    };
    /**
     * This method determines what ids have been added/removed from the previous watch.
     *
     * @private
     * @param {number[]|string[]} previous
     * What ids where previously watched.
     * @param {number[]|string[]} current
     * What ids need to be currently watched.
     * @param {(id: number|string) => string} keygen
     * The group id key generator
     * @returns
     * A map of groups that need to be added/removed from the signalR connection.
     * @memberof SignalRService
     */
    SignalRService.prototype._watchMap = function (previous, current, keygen) {
        var keep = intersection(previous, current);
        var add = difference(current, keep);
        var remove = difference(previous, keep);
        var addGroups = add.map(keygen);
        var removeGroups = remove.map(keygen);
        return {
            add: addGroups,
            remove: removeGroups,
            watched: keep.concat(add),
        };
    };
    /**
     * Subscribes or unsubscribes from groups.
     *
     * @private
     * @param {string[]} groups
     * The groups that need to be sent.
     * @param {boolean} [subscribe=true]
     * If the groups need to be watched/unwatched.
     * @returns
     * @memberof SignalRService
     */
    SignalRService.prototype._watch = function (groups, subscribe) {
        if (subscribe === void 0) { subscribe = true; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var action, proxy;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!groups.length ||
                            this._appState.user.loggedInAsHost) {
                            return [2 /*return*/];
                        }
                        action = subscribe ? 'subscribe' : 'unsubscribe';
                        proxy = this._connections.browser.proxy;
                        if (!(this._connections.browser.isDisconnected ||
                            (this._connections.browser.socket &&
                                this._connections.browser.socket.state !== 1 /* Connected */))) return [3 /*break*/, 1];
                        this._connections.browser.connected$
                            .pipe(takeUntil(this._disconnected$))
                            .subscribe(function (success) { return _this._invoke(proxy, action, groups, success); });
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, this._invoke(proxy, action, groups)];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3:
                        // update the watched groups
                        if (subscribe) {
                            groups.forEach(function (e) { return _this._watched.add(e); });
                        }
                        else {
                            groups.forEach(function (e) { return _this._watched.delete(e); });
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     *
     *
     * @private
     * @param {*} proxy
     * The signalR proxy.
     * @param {('subscribe' | 'unsubscribe')} action
     * The action that needs to be performed.
     * @param {string[]} groups
     * The groups that need to be sent.
     * @param {boolean} [success=true]
     * The default success state
     * @returns
     * @memberof SignalRService
     */
    SignalRService.prototype._invoke = function (proxy, action, groups, success) {
        var _this = this;
        if (success === void 0) { success = true; }
        if (!success) {
            return;
        }
        return new Promise(function (resolve) {
            proxy.invoke(action, groups).done(function () {
                _this._logger.log("succesfully " + action + "d the following groups: " + groups.join(','));
                _this._ngZone.run(function () { return resolve(); });
            });
        });
    };
    SignalRService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function SignalRService_Factory() { return new SignalRService(i0.ɵɵinject(i1.ServerConfiguration), i0.ɵɵinject(i2.AppStateFacade), i0.ɵɵinject(i3.Logger), i0.ɵɵinject(i0.NgZone), i0.ɵɵinject(i4.TranslateService)); }, token: SignalRService, providedIn: "root" });
    return SignalRService;
}(Mapper));
export { SignalRService };
